import { requireSupabaseClient } from '@/lib/supabaseClient'
import { buildClientPlanInsert, buildClientPlanUpdate } from '@/lib/clientPlanPayload'
import { normalizePlanSettings, PLAN_SETTINGS_VERSION } from '@/lib/planSettings'
import { track } from '@/lib/analytics'

// CRUD for the opt-in client book (public.client_plans). Every row is locked to
// the signed-in advisor by RLS (owner_id = auth.uid()), so these plain table
// calls only ever touch the caller's own clients. Cloud rows intentionally hold
// plan settings only. Employee census rows and census-derived summaries never
// enter this module or the database payload.

const CLIENT_PLAN_COLUMNS = 'id, client_name, plan_settings, plan_settings_version, created_at, updated_at'

const normalizeClientPlan = (row) => {
  if (!row) return row
  const version = row.plan_settings_version
  // Accept every version this app has ever written (rows saved by older app
  // builds keep their version until re-saved); normalizePlanSettings upgrades
  // each shape to the current one. Requiring an exact match here rejected every
  // pre-v10 row, and because listClientPlans maps over the whole result, a
  // single stale row failed the entire book with "Could not refresh".
  if (!Number.isInteger(version) || version < 1 || version > PLAN_SETTINGS_VERSION) {
    throw new Error(`Unsupported client plan version ${String(version)}.`)
  }
  return {
    ...row,
    plan_settings: normalizePlanSettings(row.plan_settings || {}, version),
  }
}

export async function listClientPlans() {
  const supabase = requireSupabaseClient()
  const { data, error } = await supabase
    .from('client_plans')
    .select(CLIENT_PLAN_COLUMNS)
    .order('updated_at', { ascending: false })
  if (error) throw error
  // A row this build cannot read is that row's problem, not the whole book's.
  // Mapping the throw straight out meant one unreadable row hid every client
  // behind "Could not refresh"; skipping it shows the rest and leaves the
  // reason in the console.
  const clients = []
  for (const row of data || []) {
    try {
      clients.push(normalizeClientPlan(row))
    } catch (rowError) {
      console.error('Skipped an unreadable client plan row:', row?.id, rowError)
    }
  }
  return clients
}

export async function getClientPlan(id) {
  const supabase = requireSupabaseClient()
  const { data, error } = await supabase
    .from('client_plans')
    .select(CLIENT_PLAN_COLUMNS)
    .eq('id', id)
    .single()
  if (error) throw error
  return normalizeClientPlan(data)
}

export async function createClientPlan({ clientName, planSettings }) {
  const supabase = requireSupabaseClient()
  // owner_id defaults to auth.uid() in the database, so we never set it here.
  const { data, error } = await supabase
    .from('client_plans')
    .insert(buildClientPlanInsert({ clientName, planSettings }))
    .select(CLIENT_PLAN_COLUMNS)
    .single()
  if (error) throw error
  // Funnel signal only. The client label can be an identifying company name, so
  // it is never sent; we record that a save happened, not what it was named.
  track('client_plan.save', { version: PLAN_SETTINGS_VERSION })
  return normalizeClientPlan(data)
}

export async function updateClientPlan(id, fields = {}) {
  const supabase = requireSupabaseClient()
  const patch = buildClientPlanUpdate(fields)

  const { data, error } = await supabase
    .from('client_plans')
    .update(patch)
    .eq('id', id)
    .select(CLIENT_PLAN_COLUMNS)
    .single()
  if (error) throw error
  return normalizeClientPlan(data)
}

export async function deleteClientPlan(id) {
  const supabase = requireSupabaseClient()
  const { error } = await supabase.from('client_plans').delete().eq('id', id)
  if (error) throw error
  track('client_plan.delete')
}

// Silent per-user autosave of the working plan settings. One row per account
// (public.plan_autosaves, keyed on owner_id), so every write upserts the same
// row. Reuses the client-book payload builder, so the census boundary and
// version are identical: only allowlisted plan settings ever leave the browser.
export async function upsertPlanAutosave({ ownerId, planSettings }) {
  const supabase = requireSupabaseClient()
  const { error } = await supabase
    .from('plan_autosaves')
    .upsert({ owner_id: ownerId, ...buildClientPlanUpdate({ planSettings }) }, { onConflict: 'owner_id' })
  if (error) throw error
}

// Read the signed-in user's one unnamed cloud draft. RLS resolves ownership;
// callers never supply an arbitrary account id. The row uses the same strict
// plan-settings normalizer as named clients and contains no census data.
export async function getPlanAutosave() {
  const supabase = requireSupabaseClient()
  const { data, error } = await supabase
    .from('plan_autosaves')
    .select('plan_settings, plan_settings_version, updated_at')
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const version = data.plan_settings_version
  if (!Number.isInteger(version) || version < 1 || version > PLAN_SETTINGS_VERSION) {
    throw new Error(`Unsupported autosave version ${String(version)}.`)
  }
  return {
    ...data,
    plan_settings: normalizePlanSettings(data.plan_settings || {}, version),
  }
}
